import { supabase } from "../supabase/SupabaseClient";

/* Tickets: pedidos y problemas, con quién lo pidió, quién lo atiende y en qué
 * quedó.
 *
 * Leer va directo a las tablas — la política de acceso de la base ya filtra
 * fila por fila lo que cada uno puede ver, así que no hace falta una función
 * intermedia que repita esa lógica (y que tarde o temprano se desincronice).
 *
 * Escribir va por funciones, siempre. Un cambio de estado no es "editar una
 * fila": es un hecho que tiene que quedar asentado en el hilo. Si fueran dos
 * operaciones sueltas, un corte en el medio dejaría el ticket movido sin que
 * nadie sepa quién lo movió.
 */

/* `origen` y `abierto_por_nombre` dicen de dónde vino el ticket y a nombre de
 * quién (un chofer por WhatsApp no tiene mail). `respuesta` y `aviso_*` son,
 * en las consultas de un chofer, lo que se le contestó y si le llegó. */
const CAMPOS_BASE =
  "id, titulo, detalle, area, prioridad, estado, sobre, abierto_por, asignado_a, creado_en, movido_en, cerrado_en, " +
  "origen, abierto_por_nombre, respuesta, respondido_en, avisado_en, aviso_ok, aviso_error";

/* `categoria` es de la migración 20261013163000. Si todavía no está aplicada,
 * la consulta falla por la columna y se lee sin ella: la ticketera no se cae
 * por un chip. */
const CAMPOS = `${CAMPOS_BASE}, categoria`;

/* 42703 = la columna no existe (Postgres); PGRST204 = PostgREST no la ve. */
const faltaColumna = (error) =>
  !!error && (error.code === "42703" || error.code === "PGRST204" || /column/i.test(error.message || ""));

async function leerTickets(armar) {
  let { data, error } = await armar(CAMPOS);
  if (faltaColumna(error)) ({ data, error } = await armar(CAMPOS_BASE));
  if (error) throw error;
  return data;
}

export async function traerAreas() {
  const { data, error } = await supabase
    .from("ticket_areas")
    .select("clave, etiqueta, ver_permiso, atender_permiso, orden")
    .order("orden");
  if (error) throw error;
  return data || [];
}

/* El área de la sesión, o null si no tiene o no se pudo saber (entonces sólo
 * ve las derivaciones que recibió o hizo). */
export async function traerMiArea() {
  const { data, error } = await supabase.rpc("mi_area_ticket");
  if (error) return null;
  return typeof data === "string" && data.trim() ? data.trim() : null;
}

export async function traerTickets() {
  // Los que se movieron hace poco arriba. Ordenar por fecha de creación deja
  // enterrado lo que pasó hoy en un ticket viejo, que es justo lo que hay que
  // mirar.
  const data = await leerTickets((cols) =>
    supabase
      .from("tickets")
      .select(cols)
      .order("movido_en", { ascending: false })
      .limit(500)
  );
  return data || [];
}

/* Un ticket solo, por id. Para el que pide el buscador (`?ver=`) y no vino en
 * los 500 de `traerTickets`: uno viejo, cerrado hace meses, sigue siendo
 * buscable y tiene que poder abrirse. La RLS filtra igual que en la lista —
 * si no lo podés ver, vuelve `null`, como si no existiera. */
export async function traerTicket(id) {
  const data = await leerTickets((cols) =>
    supabase.from("tickets").select(cols).eq("id", id).maybeSingle()
  );
  return data || null;
}

export async function traerHilo(id) {
  const { data, error } = await supabase
    .from("ticket_mensajes")
    .select("id, autor, cuerpo, cambio, creado_en")
    .eq("ticket", id)
    .order("creado_en");
  if (error) throw error;
  return data || [];
}

export async function abrirTicket({ titulo, area, detalle, prioridad, sobre }) {
  const { data, error } = await supabase.rpc("abrir_ticket", {
    p_titulo: titulo,
    p_area: area,
    p_detalle: detalle || null,
    p_prioridad: prioridad || "normal",
    p_sobre: sobre || null,
  });
  if (error) throw error;
  return data;
}

export async function comentarTicket(id, cuerpo) {
  const { error } = await supabase.rpc("comentar_ticket", {
    p_ticket: id,
    p_cuerpo: cuerpo,
  });
  if (error) throw error;
}

export async function moverTicket(id, estado) {
  const { error } = await supabase.rpc("mover_ticket", {
    p_ticket: id,
    p_estado: estado,
  });
  if (error) throw error;
}

/* mail en null desasigna. */
export async function asignarTicket(id, mail) {
  const { error } = await supabase.rpc("asignar_ticket", {
    p_ticket: id,
    p_mail: mail || null,
  });
  if (error) throw error;
}

export async function priorizarTicket(id, prioridad) {
  const { error } = await supabase.rpc("priorizar_ticket", {
    p_ticket: id,
    p_prioridad: prioridad,
  });
  if (error) throw error;
}

/* La respuesta al chofer de una consulta que abrió por WhatsApp. Queda en el
 * hilo y el ticket pasa a resuelto; el WhatsApp lo manda FlotaBot en su
 * próxima pasada (el hub no tiene desde dónde mandarlo). La base decide si
 * quien escribe puede atender el ticket. */
export async function responderAlChofer(id, texto) {
  const { error } = await supabase.rpc("responder_ticket_chofer", {
    p_ticket: id,
    p_texto: texto,
  });
  if (error) throw error;
}
